import type {
  AnnotationSize,
  AnnotationTemplate,
  Position,
} from '@/services/hooks/template_attributes'

export type LegacyAnnotationTemplate = AnnotationTemplate

export type KonvaAnnotationKind =
  | 'rect'
  | 'roundRect'
  | 'ellipse'
  | 'circle'
  | 'diamond'
  | 'triangle'
  | 'line'
  | 'arrow'
  | 'bubble'
  | 'freehand'
  | 'icon'

export type KonvaAnnotationGeometry =
  | {
      mode: 'bounds'
      x: number
      y: number
      width: number
      height: number
      rotation?: number
      cornerRadius?: number
      z?: number
    }
  | {
      mode: 'points'
      x: number
      y: number
      points: number[]
      width?: number
      height?: number
      rotation?: number
      z?: number
    }

export type KonvaAnnotationStyle = {
  stroke?: string | null
  fill?: string | null
  strokeWidth?: number
  opacity?: number
  dash?: number[]
}

export type KonvaAnnotationText = {
  enabled: boolean
  value?: string
  fontSize?: number
  fontFamily?: string
  color?: string
  align?: 'left' | 'center' | 'right'
  verticalAlign?: 'top' | 'middle' | 'bottom'
  padding?: number
  placement?: 'inside' | 'middle' | 'bottom'
  boxWidth?: number
  boxHeight?: number
  boxCornerRadius?: number
  maxLength?: number
}

export type KonvaAnnotationTemplate = {
  schema: 'konva-annotation/v1'
  kind: KonvaAnnotationKind
  geometry: KonvaAnnotationGeometry
  style: KonvaAnnotationStyle
  text?: KonvaAnnotationText
  subPageId?: string
  legacyType?: string
  bubbleValue?: string
  iconName?: string
  iconSrc?: string
}

export type PersistedAnnotationTemplate =
  | LegacyAnnotationTemplate
  | KonvaAnnotationTemplate

export type KonvaAnnotationDraftGeometry =
  | Omit<Extract<KonvaAnnotationGeometry, { mode: 'bounds' }>, 'x' | 'y'>
  | Omit<Extract<KonvaAnnotationGeometry, { mode: 'points' }>, 'x' | 'y'>

export type AnnotationToolPreset = {
  kind: KonvaAnnotationKind
  defaultGeometry: KonvaAnnotationDraftGeometry
  defaultStyle: KonvaAnnotationStyle
  defaultText?: KonvaAnnotationText
  legacyType?: string
  bubbleValue?: string
  iconName?: string
  iconSrc?: string
}

export const KONVA_ANNOTATION_SCHEMA_VERSION = 'konva-annotation/v1'

export const DEFAULT_KONVA_STROKE = '#1E1E1E'
export const DEFAULT_KONVA_STROKE_WIDTH = 2
export const DEFAULT_KONVA_TEXT_PADDING = 8
export const DEFAULT_KONVA_TEXT_FONT_SIZE = 14
export const LEGACY_ANNOTATION_BOUNDING_BOX_INSET = 8
export const LEGACY_BOUNDS_VISUAL_OFFSET = {
  x: 2,
  y: 2,
} as const
export const LEGACY_LINE_VISUAL_OFFSET = {
  x: 2,
  y: 8,
} as const

export const isKonvaAnnotationTemplate = (
  value: unknown,
): value is KonvaAnnotationTemplate =>
  Boolean(
    value &&
      typeof value === 'object' &&
      'schema' in value &&
      (value as KonvaAnnotationTemplate).schema ===
        KONVA_ANNOTATION_SCHEMA_VERSION,
  )

export const isLegacyAnnotationTemplate = (
  value: PersistedAnnotationTemplate | unknown,
): value is LegacyAnnotationTemplate => !isKonvaAnnotationTemplate(value)

export const getAnnotationSizeNumber = (size?: AnnotationSize): number => {
  if (typeof size === 'number') return size
  switch (size) {
    case 'small':
      return 24
    case 'medium':
      return 80
    case 'large':
      return 144
    case 'default':
      return 28
    default:
      return 28
  }
}

export const getLegacyStrokeWidth = (size?: AnnotationSize): number => {
  if (typeof size === 'number') {
    return Math.max(Math.round((size / 24) * 2.5 * 10) / 10, 2)
  }

  switch (size) {
    case 'small':
      return 3
    case 'medium':
      return 4.2
    case 'large':
      return 4.5
    case 'default':
      return 2.5
    default:
      return 2.5
  }
}

export const getLegacyStrokeWidthForType = ({
  size,
  type,
}: {
  size?: AnnotationSize
  type?: string
}): number => {
  const baseStrokeWidth = getLegacyStrokeWidth(size)

  if (!type) return baseStrokeWidth

  if (type === 'line' || type.includes('arrow') || type.includes('bubble')) {
    return Math.max(Math.round(baseStrokeWidth * 0.9 * 10) / 10, 2)
  }

  if (
    type === 'circle' ||
    type === 'square' ||
    type === 'triangle' ||
    type === 'diamond'
  ) {
    return Math.round(baseStrokeWidth * 1.1 * 10) / 10
  }

  return baseStrokeWidth
}

export const makeBoundsGeometry = ({
  x,
  y,
  width,
  height,
  rotation,
  cornerRadius,
  z,
}: {
  x: number
  y: number
  width: number
  height: number
  rotation?: number
  cornerRadius?: number
  z?: number
}): Extract<KonvaAnnotationGeometry, { mode: 'bounds' }> => ({
  mode: 'bounds',
  x,
  y,
  width,
  height,
  rotation,
  cornerRadius,
  z,
})

export const makePointsGeometry = ({
  x,
  y,
  points,
  width,
  height,
  rotation,
  z,
}: {
  x: number
  y: number
  points: number[]
  width?: number
  height?: number
  rotation?: number
  z?: number
}): Extract<KonvaAnnotationGeometry, { mode: 'points' }> => ({
  mode: 'points',
  x,
  y,
  points,
  width,
  height,
  rotation,
  z,
})

export const getAnnotationZ = (position?: Position) => position?.z ?? 1000
